import { ReportHeader } from "views/applications-id-report/components/ReportHeader";
import { Section } from "views/applications-id-report/components/Section";
import { has, toMoney } from "shared/utils";
import { DateTime } from "luxon";
import { Hide, Show } from "shared/components";
import {
  CombinedIncomeReport,
  EditIncomeModalData,
  EditTransactionsDrawerData,
  OverrideIncomeModalData
} from "./types";
import { NetIncomeChart } from "./slices/NetIncomeGraphSlice";
import {
  ApplicationStatus,
  ExecutionModes,
  IncomeStatus,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { DefinedQueryObserverResult, useQuery } from "@tanstack/react-query";
import { VerificationReport } from "types/landlord/reports";
import { getVerificationQueryKeys } from "views/applications-id-report/utils/getVerificationQueryKeys";
import api from "api/landlord/applications";
import { Loader } from "shared/components/Loader";
import { ReportException } from "views/applications-id-report/components/ReportException";
import { Applicant, Application } from "types/application";
import { mapNetIncomeAnalysis, mapPayrollConnectionFailure } from "./helpers";
import { ExpandableTable } from "./components/UniversalTable";
import { activityLogsQueryKey } from "shared/queries/useActivityLogsController";
import { AdjustedApplicationRentActivity } from "shared/app/ActivityLog/types";
import { instance } from "api/apiBase";
import queryString from "qs";
import { AdjustedApplicationRentActivityLog } from "views/applications-id-report/components/AdjustedApplicationRentIncomeSection";
import { IncomeSlice } from "./slices/TablesSlice/IncomeSlice";
import { DocumentWarnings } from "./slices/DocumentWarningsSlice";
import { PrintReady } from "views/applications-id-report/providers/PrintProvider";
import { getAdditionalDataPerVerificationType } from "./utils";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { EditIncomeModal } from "./components/EditIncomeModal";
import { ReverifyIncomeModal } from "./components/ReverifyIncomeModal";
import { NoIncomeSection } from "./components/NoIncomeSection";
import { NetIncomeAnalysisExpandedRow } from "./components/NetIncomeAnalysisExpandedRow";
import { Verification } from "shared/types";
import { PendingReportView } from "views/applications-id-report/components/ReportsFallback/PendingReportView";
import { UnavailableReportView } from "views/applications-id-report/components/ReportsFallback/UnavailableReportView";
import IncomeSummary from "./components/IncomeSummary";
import { VerificationOverrideModal } from "./components/VerificationOverrideModal";
import { SkippedReportView } from "views/applications-id-report/components/ReportsFallback/SkippedReportView";
import { DebtDetails } from "./components/DebtDetails";
import {
  COMPLETE_VERIFICATION_STATUSES,
  INCOMPLETE_APPLICATION_STATUSES,
  INCOMPLETE_VERIFICATION_STATUSES
} from "./constants";
import { Banner } from "shared/components/Banner";
import { HugeiconsIcon } from "@hugeicons/react";
import { AlertCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { EditTransactionsDrawer } from "./components/EditTransactionsDrawer";
import {
  getIncomeReverifyRequest,
  getLatestReverifyRequestForIncome,
  getReverifyRequests
} from "./slices/ReverifySlice/utils";

export const CombinedIncomeReportSlice = ({
  verificationId,
  applicantId,
  applicationId,
  hasCreditReport,
  applicant,
  applicationStatus,
  currentVerification,
  applicationMeta
}: {
  verificationId?: number;
  applicantId?: number;
  applicationId?: number;
  hasCreditReport?: boolean;
  applicant: Applicant;
  applicationStatus: ApplicationStatus;
  currentVerification: Verification;
  applicationMeta: DefinedQueryObserverResult<Application | undefined, unknown>;
}) => {
  const [showReverifyIncomeModal, setShowReverifyIncomeModal] = useState<
    number | false
  >(false);
  const [showEditIncomeModal, setShowEditIncomeModal] = useState<
    false | EditIncomeModalData
  >(false);
  const [showOverrideVerificationModal, setShowOverrideVerificationModal] =
    useState<false | OverrideIncomeModalData>(false);
  const [showEditTransactionsDrawer, setShowEditTransactionsDrawer] = useState<
    false | EditTransactionsDrawerData
  >(false);
  const [hideProjectedDebtSection, setHideProjectedDebtSection] =
    useState(false);

  const {
    data: reportData,
    isError,
    isLoading,
    refetch
  } = useQuery<VerificationReport<CombinedIncomeReport>>({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.CombinedIncome,
      applicationId: Number(applicationId || 0),
      applicantId: Number(applicantId || 0),
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          Number(applicationId || 0),
          Number(applicantId || 0),
          verificationId || 0
        )
        .then((data) => data.verification)
  });

  const applicationRentActivityLogsMeta = useQuery<
    AdjustedApplicationRentActivity[]
  >({
    queryKey: activityLogsQueryKey(applicant.application_id),
    queryFn: () => {
      const params = {
        events: [
          "application_rent_adjusted",
          "application_rent_automatically_adjusted"
        ]
      };
      const url =
        `portal/v1/applications/${applicant.application_id}/activity_logs?` +
        queryString.stringify(params, { arrayFormat: "brackets" });
      return instance.get(url).then(({ data }) => data.activity_logs);
    },
    enabled: true
  });

  const incomeSourceProvidersMap = useMemo(
    () =>
      Object.fromEntries(
        (reportData?.income_sources ?? []).map((s) => [s.id, s.provider ?? ""])
      ),
    [reportData?.income_sources]
  );

  const incomeSourceTrustScoresMap = useMemo(
    () =>
      Object.fromEntries(
        (reportData?.report?.incomes ?? [])
          .flatMap((income) => income.sources ?? [])
          .filter((s) => s.trust_score !== undefined)
          .map((s) => [s.id, s.trust_score])
      ) as Record<number, number>,
    [reportData?.report?.incomes]
  );

  const data = reportData?.report;

  // Because status does not yet exist on data.bank_statements
  const filteredBankStatements = useMemo(() => {
    if (!data?.bank_statements) return {};

    const resetBankStatementIds = new Set(
      reportData?.income_sources
        ?.filter((incomeSource) => incomeSource.status === "reset")
        .map((incomeSource) => incomeSource!.id)
    );

    return Object.fromEntries(
      Object.entries(data.bank_statements).filter(
        ([id]) => !resetBankStatementIds.has(+id)
      )
    );
  }, [reportData?.income_sources]);

  if (isLoading) return <Loader />;
  if (isError)
    return (
      <Section maxWidth="none" sectionTitle="Income summary">
        <ReportException
          title="Failed to load income report"
          applicationStatus={applicationStatus}
          applicationId={applicationId || "0"}
        />
      </Section>
    );

  if (
    !reportData ||
    !data ||
    !Object.keys(data).length ||
    !applicationMeta.data
  )
    return null;

  const settings = reportData?.settings;
  const {
    pendingReverifyRequests,
    completedReverifyRequests,
    failedReverifyRequests
  } = getReverifyRequests(reportData);

  if (
    currentVerification?.status === VerificationStatus.Skipped &&
    currentVerification.settings?.execution_mode ===
      ExecutionModes.Conditionally
  )
    return (
      <SkippedReportView
        refetch={applicationMeta.refetch}
        applicantId={applicant.id}
        verificationId={currentVerification.id}
        applicationId={applicationMeta.data.id}
        verificationStatus={currentVerification.status}
      />
    );

  if (currentVerification?.status === VerificationStatus.Canceled)
    return <PendingReportView message="Report was canceled" />;

  if (currentVerification?.status === VerificationStatus.NoDataAvailable)
    return <UnavailableReportView />;

  const noIncome = data.incomes.find((income) => income.type === "no_income");
  if (noIncome) {
    const housingAssistance = data.incomes.find(
      (income) => income.type === "housing_assistance"
    );

    return (
      <NoIncomeSection
        noIncome={noIncome}
        housingAssistance={housingAssistance}
        applicant={applicant}
        verificationId={verificationId}
      />
    );
  }

  const summary = data.total_summary,
    netIncomeData = data.net_income_analysis,
    bankStatements = data.bank_statements;

  const verificationStatus = reportData?.status ?? VerificationStatus.Pending;
  const isVerificationIncomplete =
    INCOMPLETE_VERIFICATION_STATUSES.includes(verificationStatus) &&
    INCOMPLETE_APPLICATION_STATUSES.includes(applicationMeta.data.status);

  const payrollConnectionFailure = mapPayrollConnectionFailure(
    data.payroll_connection_failure
  );

  // Hide unfinished incomes if the combined verification is complete
  const incomes = COMPLETE_VERIFICATION_STATUSES.includes(verificationStatus)
    ? data.incomes.filter((income) => income.status !== IncomeStatus.Pending)
    : data.incomes;

  return (
    <div>
      <ReportHeader
        applicationStatus={applicationStatus}
        applicationId={applicationId || "0"}
        title="Income history"
        applicantId={applicantId || 0}
        name={VerificationNames.CombinedIncome}
        reportDate={
          isVerificationIncomplete ? undefined : currentVerification.updated_at
        }
        hidePrint={isVerificationIncomplete}
      />

      <Show if={verificationId}>
        <PrintReady verificationId={verificationId!} />
      </Show>

      <Show if={Boolean(payrollConnectionFailure)}>
        <Banner
          variant="danger"
          textTheme="base"
          className="mb-6 rounded-lg"
          title={payrollConnectionFailure?.title}
          body={payrollConnectionFailure?.subtitle}
          HugeIcon={
            <HugeiconsIcon
              icon={AlertCircleIcon}
              className="shrink-0 text-danger-500"
              size="20"
            />
          }
        />
      </Show>

      <Section
        sectionTitle="Income summary"
        maxWidth="100%"
        sectionClassName="p-0"
      >
        <IncomeSummary
          incomes={incomes}
          pendingReverifyRequests={pendingReverifyRequests}
          completedReverifyRequests={completedReverifyRequests}
          summary={summary}
          combinedIncomeReport={data}
          hasCreditReport={Boolean(hasCreditReport)}
          verificationStatus={verificationStatus}
          isSummaryPending={isVerificationIncomplete}
        />
      </Section>

      <DocumentWarnings
        incomes={incomes}
        failedReverifyRequests={failedReverifyRequests}
        bankStatements={filteredBankStatements}
      />

      <Hide if={isVerificationIncomplete}>
        <Section sectionTitle="Net income analysis" maxWidth="1100px">
          <NetIncomeChart data={netIncomeData} />

          <div className="mt-[60px]">
            <ExpandableTable
              hideHeader
              rowClassName="items-center pr-4"
              headerRowClassName="text-content-1000 text-sm font-medium"
              columns={[
                {
                  header: "month",
                  accessor: (row) =>
                    DateTime.fromISO(row.month).toFormat("MMMM, yyyy"),
                  widthClass: "w-full"
                },
                {
                  header: "total",
                  accessor: (row) => toMoney(row.total),
                  widthClass: "w-[120px] shrink-0"
                }
              ]}
              rows={mapNetIncomeAnalysis(netIncomeData)}
              renderExpanded={(original) => (
                <NetIncomeAnalysisExpandedRow original={original} />
              )}
            />
          </div>
        </Section>
      </Hide>

      {incomes.map((income, index) => {
        const pendingIncomeReverifyRequest = getIncomeReverifyRequest(
          income.id,
          pendingReverifyRequests
        );
        const completedReverifyRequest = getLatestReverifyRequestForIncome(
          income.id,
          completedReverifyRequests
        );
        const failedReverifyRequest = getLatestReverifyRequestForIncome(
          income.id,
          failedReverifyRequests
        );

        return (
          <IncomeSlice
            key={index}
            income={income}
            applicationId={applicationId}
            applicationStatus={applicationStatus}
            settings={settings}
            additionalData={getAdditionalDataPerVerificationType(
              data,
              income,
              filteredBankStatements
            )}
            setShowEditIncomeModal={setShowEditIncomeModal}
            setShowEditTransactionsDrawer={setShowEditTransactionsDrawer}
            setShowReverifyIncomeModal={setShowReverifyIncomeModal}
            setShowOverrideVerificationModal={setShowOverrideVerificationModal}
            isManualApplicant={applicant.is_manual}
            setHideProjectedDebtSection={setHideProjectedDebtSection}
            verificationId={verificationId}
            applicantId={applicantId}
            pendingReverifyRequest={pendingIncomeReverifyRequest}
            completedReverifyRequest={completedReverifyRequest}
            failedReverifyRequest={failedReverifyRequest}
            verificationStatus={reportData?.status}
            onReverifyRequestCancelled={refetch}
            applicant={applicant}
            incomeSourceProviders={incomeSourceProvidersMap}
            incomeSourceTrustScores={incomeSourceTrustScoresMap}
          />
        );
      })}

      <Hide
        if={
          hideProjectedDebtSection ||
          Boolean(
            !summary.debt_gross_income_ratio &&
            !summary.debt_net_income_ratio &&
            !summary.debt_self_attested_gross_income_ratio
          )
        }
      >
        <DebtDetails
          summary={summary}
          isVerificationIncomplete={isVerificationIncomplete}
        />
      </Hide>

      <div className="space-y-8 p-6 pt-0 text-sm">
        {applicationRentActivityLogsMeta.data?.map((activityItem, index) => (
          <AdjustedApplicationRentActivityLog
            key={index}
            activityItem={activityItem}
          />
        ))}
      </div>

      <EditTransactionsDrawer
        onSuccess={refetch}
        applicationId={Number(applicationId || 0)}
        applicant={applicant}
        verificationId={verificationId || 0}
        opened={Boolean(showEditTransactionsDrawer)}
        onClose={() => setShowEditTransactionsDrawer(false)}
        allBankStatements={bankStatements}
        {...(typeof showEditTransactionsDrawer === "object"
          ? showEditTransactionsDrawer
          : {})}
      />

      <Show if={Boolean(showEditIncomeModal)}>
        <EditIncomeModal
          onSuccess={refetch}
          applicationId={Number(applicationId || 0)}
          applicant={applicant}
          verificationId={verificationId || 0}
          opened={Boolean(showEditIncomeModal)}
          onClose={() => setShowEditIncomeModal(false)}
          {...(typeof showEditIncomeModal === "object"
            ? showEditIncomeModal
            : {})}
        />
      </Show>

      <Show if={has(showOverrideVerificationModal)}>
        <VerificationOverrideModal
          onSuccess={() => {
            toast.success("Successfully overrode verification");
            refetch();
            setShowOverrideVerificationModal(false);
          }}
          applicationId={Number(applicationId || 0)}
          applicant={applicant}
          verificationId={verificationId || 0}
          opened={Boolean(showOverrideVerificationModal)}
          onClose={() => setShowOverrideVerificationModal(false)}
          {...(typeof showOverrideVerificationModal === "object"
            ? showOverrideVerificationModal
            : {})}
        />
      </Show>

      <Show if={showReverifyIncomeModal}>
        <ReverifyIncomeModal
          onSuccess={() => {
            toast.success("Re-verification requested successfully");
            refetch();
          }}
          settings={settings}
          applicant={applicant}
          applicationId={applicationId || 0}
          verificationId={verificationId || 0}
          opened={has(showReverifyIncomeModal)}
          onClose={() => {
            setShowReverifyIncomeModal(false);
          }}
          income={incomes.find((i) => i.id === showReverifyIncomeModal)}
        />
      </Show>
    </div>
  );
};
